import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCurrency } from "@/lib/currencies";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Bell, 
  Check, 
  AlertTriangle, 
  Calendar,
  Euro,
  Loader2,
  Mail
} from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [paymentData, setPaymentData] = useState({
    expected_amount: 0,
    paid_amount: "",
    payment_method: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const { t } = useLanguage();

  const fetchReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reminders`);
      setReminders(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Erinnerungen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleOpenPaymentDialog = (reminder) => {
    setSelectedReminder(reminder);
    setPaymentData({
      expected_amount: reminder.expense?.amount || 0,
      paid_amount: (reminder.expense?.amount || 0).toString(),
      payment_method: "",
      notes: "",
    });
    setPaymentDialogOpen(true);
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Create payment record
      await axios.post(`${API}/payments`, {
        expense_id: selectedReminder.expense_id,
        expected_amount: parseFloat(paymentData.expected_amount),
        paid_amount: parseFloat(paymentData.paid_amount),
        paid_date: format(new Date(), "yyyy-MM-dd"),
        payment_method: paymentData.payment_method || null,
        notes: paymentData.notes || null,
      });

      toast.success("Zahlung bestätigt");
      setPaymentDialogOpen(false);
      fetchReminders();
    } catch (error) {
      toast.error("Fehler beim Bestätigen der Zahlung");
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledgeReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}/acknowledge`);
      toast.success("Erinnerung bestätigt");
      fetchReminders();
    } catch (error) {
      toast.error("Fehler beim Bestätigen");
    }
  };

  const getDaysUntil = (dateStr) => {
    return differenceInDays(parseISO(dateStr), new Date());
  };

  const getUrgencyBadge = (daysUntil) => {
    if (daysUntil < 0) {
      return <Badge className="bg-red-500 text-white">{t("reminders.overdue")}</Badge>;
    } else if (daysUntil <= 3) {
      return <Badge className="bg-red-500 text-white">{t("reminders.urgent")}</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge className="bg-amber-500 text-white">{t("reminders.dueSoon")}</Badge>;
    }
    return <Badge variant="secondary">{daysUntil} {t("reminders.days")}</Badge>;
  };

  const pendingReminders = reminders.filter(r => !r.is_acknowledged);
  const acknowledgedReminders = reminders.filter(r => r.is_acknowledged);
  const paymentReminders = pendingReminders.filter(r => r.reminder_type === "payment_due");
  const cancellationReminders = pendingReminders.filter(r => r.reminder_type === "cancellation");

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-24 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("reminders.title")}</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            {t("reminders.subtitle")}
          </p>
        </div>
        <Button 
          variant="outline"
          onClick={async () => {
            try {
              await axios.post(`${API}/email/send-reminders`);
              toast.success("E-Mail-Erinnerungen versendet");
            } catch (error) {
              if (error.response?.status === 400) {
                toast.error("E-Mail nicht konfiguriert");
              } else {
                toast.error("Fehler beim Versenden");
              }
            }
          }}
          data-testid="send-reminders-btn"
        >
          <Mail className="h-4 w-4 mr-2" />
          {t("reminders.sendEmailReminders")}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Card className="card-hover col-span-2 md:col-span-1" data-testid="stat-pending">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("reminders.openReminders")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">{pendingReminders.length}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Bell className="h-5 w-5 md:h-6 md:w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-payments">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("reminders.duePayments")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">{paymentReminders.length}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                <Euro className="h-5 w-5 md:h-6 md:w-6 text-[#E31E24]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-cancellations">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("reminders.cancellationDeadlines")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">{cancellationReminders.length}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            {t("reminders.open")} ({pendingReminders.length})
          </TabsTrigger>
          <TabsTrigger value="acknowledged" data-testid="tab-acknowledged">
            {t("reminders.done")} ({acknowledgedReminders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {pendingReminders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("reminders.noOpenReminders")}</p>
                <p className="text-sm">{t("reminders.allDone")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingReminders.map((reminder) => {
                const daysUntil = getDaysUntil(reminder.due_date);
                const isPayment = reminder.reminder_type === "payment_due";
                
                return (
                  <Card 
                    key={reminder.id} 
                    className={`card-hover ${daysUntil < 0 ? "border-red-500" : daysUntil <= 3 ? "border-amber-500" : ""}`}
                    data-testid={`reminder-${reminder.id}`}
                  >
                    <CardContent className="p-4 md:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 md:gap-4 min-w-0 flex-1">
                          <div className={`h-8 w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isPayment ? "bg-[#E31E24]/10" : "bg-red-500/10"
                          }`}>
                            {isPayment ? (
                              <Euro className="h-4 w-4 md:h-5 md:w-5 text-[#E31E24]" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-sm md:text-base truncate">{reminder.expense?.title || "Unbekannt"}</h3>
                            <p className="text-xs md:text-sm text-muted-foreground mt-1">
                              {isPayment ? t("reminders.paymentReminder") : t("reminders.cancellationDeadline")}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs md:text-sm">
                                {t("reminders.dueDate")}: {format(parseISO(reminder.due_date), "dd.MM.yyyy", { locale: de })}
                              </span>
                            </div>
                            {isPayment && reminder.expense?.amount && (
                              <p className="text-base md:text-lg font-bold font-[JetBrains_Mono] mt-2">
                                {formatCurrency(reminder.expense.amount, reminder.expense.currency)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          {getUrgencyBadge(daysUntil)}
                          {isPayment ? (
                            <Button
                              size="sm"
                              className="bg-[#E31E24] hover:bg-[#c91a1f] mt-2"
                              onClick={() => handleOpenPaymentDialog(reminder)}
                              data-testid={`pay-${reminder.id}`}
                            >
                              {t("reminders.paid")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2"
                              onClick={() => handleAcknowledgeReminder(reminder.id)}
                              data-testid={`acknowledge-${reminder.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              {t("reminders.done")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="acknowledged" className="mt-6">
          {acknowledgedReminders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Check className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("reminders.noDoneReminders")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {acknowledgedReminders.map((reminder) => (
                <Card 
                  key={reminder.id} 
                  className="opacity-60"
                  data-testid={`reminder-done-${reminder.id}`}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{reminder.expense?.title || "Unbekannt"}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {reminder.reminder_type === "payment_due" ? t("reminders.paid") : t("reminders.done")}
                        </p>
                        {reminder.acknowledged_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t("reminders.doneAt")}: {format(parseISO(reminder.acknowledged_at), "dd.MM.yyyy", { locale: de })}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{t("reminders.confirmPayment")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfirmPayment} className="space-y-4 mt-4">
            <div>
              <Label>{t("reminders.expense")}</Label>
              <p className="text-sm font-medium mt-1">{selectedReminder?.expense?.title}</p>
            </div>
            <div>
              <Label htmlFor="expected">{t("reminders.expectedAmount")}</Label>
              <Input
                id="expected"
                value={formatCurrency(paymentData.expected_amount)}
                disabled
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="paid">{t("reminders.paidAmount")} *</Label>
              <Input
                id="paid"
                type="number"
                step="0.01"
                min="0"
                value={paymentData.paid_amount}
                onChange={(e) => setPaymentData({ ...paymentData, paid_amount: e.target.value })}
                required
                data-testid="payment-amount"
              />
              {parseFloat(paymentData.paid_amount) !== paymentData.expected_amount && (
                <p className="text-xs text-amber-600 mt-1">
                  {t("reminders.deviation")}: {formatCurrency(parseFloat(paymentData.paid_amount) - paymentData.expected_amount)}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="method">{t("reminders.paymentMethod")}</Label>
              <Select
                value={paymentData.payment_method}
                onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method">
                  <SelectValue placeholder={t("reminders.selectMethod")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">{t("reminders.bankTransfer")}</SelectItem>
                  <SelectItem value="direct_debit">{t("reminders.directDebit")}</SelectItem>
                  <SelectItem value="credit_card">{t("reminders.creditCard")}</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="cash">{t("reminders.cash")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">{t("reminders.notes")}</Label>
              <Textarea
                id="notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder={t("reminders.additionalInfo")}
                rows={2}
                data-testid="payment-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button 
                type="submit" 
                className="bg-[#E31E24] hover:bg-[#c91a1f]"
                disabled={saving}
                data-testid="confirm-payment-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("reminders.confirm")}...
                  </>
                ) : (
                  t("reminders.confirmPayment")
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
